import React, { useState, useRef, useEffect } from 'react';
import AddressField from './form-fields/AddressField';
import RadiusDropdown from './form-fields/RadiusDropdown';
import PriceRangeDropdown from './form-fields/PriceRangeDropdown';
import BedroomsDropdown from './form-fields/BedroomsDropdown';
import HouseTypeDropdown from './form-fields/HouseTypeDropdown';
import AdditionalFiltersDropdown from './form-fields/AdditionalFiltersDropdown';
import MobileFiltersOverlay from './MobileFiltersOverlay';

function SearchForm({
  maxData,
  filterData,
  filters,
  setFilters,
  onSearch,
  isSearching,
  buildQueryString,
  viewMode,
  searchMode,
}) {
  const MAPBOX_TOKEN = window.MB_TOKEN || '';

  const [radiusOpen, setRadiusOpen] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [houseTypeOpen, setHouseTypeOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false);

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const [isSticky, setIsSticky] = useState(false);
  const [formHeight, setFormHeight] = useState(0);
  const sentinelRef = useRef(null);
  const articleRef = useRef(null);

  // Ref for outside click detection
  const formRef = useRef(null);

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setRadiusOpen(false);
    setPriceOpen(false);
    setHouseTypeOpen(false);
    setFiltersOpen(false);
    setAdditionalFiltersOpen(false);
  };

  // Handle outside clicks
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (formRef.current && !formRef.current.contains(event.target)) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Handle body scroll locking for mobile filters
  useEffect(() => {
    if (mobileFiltersOpen) {
      // Prevent body scroll when mobile filters are open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when mobile filters are closed
      document.body.style.overflow = '';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileFiltersOpen]);

  // Handle sticky behavior with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldBeSticky = !entry.isIntersecting;

        if (shouldBeSticky && articleRef.current && !isSticky) {
          setFormHeight(articleRef.current.offsetHeight);
        }

        setIsSticky(shouldBeSticky);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, []);

  const handleAddressSelect = (address, coordinates) => {
    setFilters((prev) => ({
      ...prev,
      address,
      coordinates,
    }));
  };

  const toggleDropdown = (dropdown) => {
    switch (dropdown) {
      case 'radius':
        if (radiusOpen) {
          setRadiusOpen(false);
        } else {
          closeAllDropdowns();
          setRadiusOpen(true);
        }
        break;
      case 'price':
        if (priceOpen) {
          setPriceOpen(false);
        } else {
          closeAllDropdowns();
          setPriceOpen(true);
        }
        break;
      case 'houseType':
        if (houseTypeOpen) {
          setHouseTypeOpen(false);
        } else {
          closeAllDropdowns();
          setHouseTypeOpen(true);
        }
        break;
      case 'filters':
        if (filtersOpen) {
          setFiltersOpen(false);
        } else {
          closeAllDropdowns();
          setFiltersOpen(true);
        }
        break;
      case 'additionalFilters':
        if (additionalFiltersOpen) {
          setAdditionalFiltersOpen(false);
        } else {
          closeAllDropdowns();
          setAdditionalFiltersOpen(true);
        }
        break;
    }
  };

  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    closeAllDropdowns();
    setMobileFiltersOpen(false);
    onSearch(filters);
    buildQueryString(filters, searchMode, viewMode);
  };

  const handleInputChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    setFilters((prev) => {
      const current = prev[field];
      const updated = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  return (
    <div className="md:mb-11 relative">
      {/* Sentinel element to detect when to make sticky - visible on mobile */}
      <div ref={sentinelRef} className="h-px" />

      {/* Spacer to maintain layout when form becomes fixed */}
      {isSticky && (
        <div className="lg:hidden" style={{ height: `${formHeight}px` }} />
      )}

      <article
        ref={articleRef}
        className={`2xl:w-[1228px] mx-auto w-full bg-grey-100 lg:bg-white lg:border-2 xl:border-r-0 border-[#ebebeb] p-2 lg:p-0 transition-all duration-200 ${
          isSticky
            ? 'lg:relative fixed top-0 left-0 right-0 z-40 shadow-lg'
            : 'relative'
        }`}
      >
        <form
          ref={formRef}
          method="get"
          action="/property-search"
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,303px)_1fr_94px] gap-2 lg:gap-0 items-center w-full h-full">
            <div className="flex gap-2">
              <AddressField
                address={filters.address}
                onAddressChange={(value) => handleInputChange('address', value)}
                onAddressSelect={handleAddressSelect}
                MAPBOX_TOKEN={MAPBOX_TOKEN}
              />

              {/* Mobile Filters Toggle Button */}
              <div className="lg:hidden col-span-full">
                <button
                  type="button"
                  onClick={toggleMobileFilters}
                  className="w-[105px] h-full text-white bg-blue-700 flex items-center justify-center gap-2 text-sm font-medium py-3"
                >
                  Filters
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="7"
                    viewBox="0 0 10 7"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                    style={{
                      transform: mobileFiltersOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                  >
                    <use href="#icon-chevron-down" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="hidden lg:grid lg:grid-cols-5 xl:grid-cols-[134px_229px_176px_171px_131px] h-full gap-2 lg:gap-0">
              {/* Radius Dropdown */}
              <RadiusDropdown
                radius={filters.radius}
                onRadiusChange={(value) => handleInputChange('radius', value)}
                isOpen={radiusOpen}
                onToggle={() => toggleDropdown('radius')}
              />

              {/* Price Range Dropdown */}
              <PriceRangeDropdown
                maxPriceData={maxData.price ?? 2000000}
                minPrice={filters.minPrice}
                maxPrice={filters.maxPrice}
                onMinPriceChange={(value) =>
                  handleInputChange('minPrice', value)
                }
                onMaxPriceChange={(value) =>
                  handleInputChange('maxPrice', value)
                }
                isOpen={priceOpen}
                onToggle={() => toggleDropdown('price')}
              />

              {/* Filters (Bedrooms) Dropdown */}
              <BedroomsDropdown
                maxBedroomsData={maxData.bedrooms ?? 10}
                minBedrooms={filters.minBedrooms}
                maxBedrooms={filters.maxBedrooms}
                onMinBedroomsChange={(value) =>
                  handleInputChange('minBedrooms', value)
                }
                onMaxBedroomsChange={(value) =>
                  handleInputChange('maxBedrooms', value)
                }
                isOpen={filtersOpen}
                onToggle={() => toggleDropdown('filters')}
              />

              {/* House Type Dropdown - Disabled for developments */}
              <HouseTypeDropdown
                houseTypeData={filterData.houseTypes}
                selectedHouseTypes={filters.houseTypes}
                onHouseTypeChange={(value) =>
                  handleCheckboxChange('houseTypes', value)
                }
                isOpen={houseTypeOpen}
                onToggle={() => toggleDropdown('houseType')}
                disabled={searchMode === 'developments'}
              />

              {/* Additional Filters Dropdown - Disabled for developments */}
              <AdditionalFiltersDropdown
                filterData={filterData}
                selectedFeatures={filters.features}
                selectedBuyingSchemes={filters.buyingSchemes}
                onFeatureChange={(value) =>
                  handleCheckboxChange('features', value)
                }
                onBuyingSchemeChange={(value) =>
                  handleCheckboxChange('buyingSchemes', value)
                }
                isOpen={additionalFiltersOpen}
                onToggle={() => toggleDropdown('additionalFilters')}
                disabled={searchMode === 'developments'}
              />
            </div>

            <button
              type="submit"
              disabled={isSearching}
              className="hidden lg:flex bg-pink-400 hover:bg-blue-700 transition-colors justify-self-end w-full h-15 xl:h-[calc(100%+4px)] xl:-mr-[2px] items-center justify-center disabled:opacity-50"
            >
              {isSearching ? (
                <div role="status">
                  <svg
                    aria-hidden="true"
                    class="w-8 h-8 text-white animate-spin fill-pink-400"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span class="sr-only">Loading...</span>
                </div>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  aria-hidden="true"
                  focusable="false"
                  className="hidden xl:block"
                >
                  <use href="#icon-search" />
                </svg>
              )}
              <span className="color-blue-700 tracking-wider text-xl xl:sr-only">
                {isSearching ? 'Searching...' : 'Search'}
              </span>
            </button>
          </div>
        </form>

        {/* Mobile Filters Overlay */}
        <MobileFiltersOverlay
          isOpen={mobileFiltersOpen}
          onClose={toggleMobileFilters}
          searchMode={searchMode}
          maxData={maxData}
          filterData={filterData}
          filters={filters}
          onAddressChange={(value) => handleInputChange('address', value)}
          onAddressSelect={handleAddressSelect}
          MAPBOX_TOKEN={MAPBOX_TOKEN}
          onRadiusChange={(value) => handleInputChange('radius', value)}
          onMinPriceChange={(value) => handleInputChange('minPrice', value)}
          onMaxPriceChange={(value) => handleInputChange('maxPrice', value)}
          onMinBedroomsChange={(value) =>
            handleInputChange('minBedrooms', value)
          }
          onMaxBedroomsChange={(value) =>
            handleInputChange('maxBedrooms', value)
          }
          onHouseTypeChange={(value) =>
            handleCheckboxChange('houseTypes', value)
          }
          onFeatureChange={(value) => handleCheckboxChange('features', value)}
          onBuyingSchemeChange={(value) =>
            handleCheckboxChange('buyingSchemes', value)
          }
          onSubmit={handleSubmit}
        />
      </article>
    </div>
  );
}

export default SearchForm;
